import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchMe, logout as apiLogout, loginWithGoogle as apiLoginWithGoogle, type CurrentUser } from '../api/auth';
import { ApiError } from '../api/client';

type Status = 'loading' | 'authed' | 'guest';

interface AuthContextValue {
  status: Status;
  user: CurrentUser | null;
  loginWithGoogle: (idToken: string) => Promise<CurrentUser>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [optimisticUser, setOptimisticUser] = useState<CurrentUser | null>(null);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async ({ signal }) => {
      try {
        const { user } = await fetchMe(signal);
        return user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const user = optimisticUser ?? meQuery.data ?? null;

  const status: Status = meQuery.isLoading
    ? 'loading'
    : user
      ? 'authed'
      : 'guest';

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      const { user: u } = await apiLoginWithGoogle(idToken);
      setOptimisticUser(u);
      queryClient.setQueryData(['auth', 'me'], u);
      return u;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await apiLogout();
    setOptimisticUser(null);
    queryClient.setQueryData(['auth', 'me'], null);
    queryClient.removeQueries();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, loginWithGoogle, logout }),
    [status, user, loginWithGoogle, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
